import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  IconButton,
  Spinner,
  Text,
  Textarea as ChakraTextarea,
  Tooltip,
  useClipboard,
  useEditableControls,
  useToast,
} from '@chakra-ui/react';
import { taskAction } from 'actions';
import MarkdownRenderer from 'components/MarkdownRenderer';
import { TEXT_CONSTANT } from 'helpers/constant';
import { timeFormat } from 'helpers/date';
import { TASK_STATUS } from 'helpers/enum';
import { exportAnswerToWord } from 'helpers/exportToWord';
import { Check, Copy, FileDown, Pencil } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Model } from 'services/model/model.type';
import { User } from 'types';
import { Message } from 'types/reducers/task';

import TypingText from './TypingText';

interface MessageChatProps {
  data: Message;
  status: TASK_STATUS;
  isLast: boolean;
  user: User;
  cancelEditRef: React.RefObject<HTMLButtonElement>;
}

interface EditableTextareaProps {
  value: string;
  onChange: (value: string) => void;
}

const EditableTextarea = ({ value, onChange: onChangeProp }: EditableTextareaProps) => {
  const { isEditing } = useEditableControls();
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '0px';
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 20;
      const minHeight = lineHeight + 10;
      textarea.style.height = `${Math.max(minHeight, Math.min(scrollHeight, 500))}px`;
    }
  }, []);

  React.useEffect(() => {
    if (isEditing) {
      const timer = setTimeout(() => {
        adjustHeight();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isEditing, adjustHeight]);

  React.useEffect(() => {
    if (isEditing) {
      const timer = setTimeout(adjustHeight, 0);
      return () => clearTimeout(timer);
    }
  }, [value, isEditing, adjustHeight]);

  if (!isEditing) {
    return null;
  }

  const lineCount = value ? value.split('\n').length : 1;
  const estimatedHeight = Math.max(40, lineCount * 20 + 10);

  return (
    <ChakraTextarea
      ref={textareaRef}
      sx={{
        height: 'auto',
        minHeight: `${estimatedHeight}px`,
        lineHeight: '1.4',
      }}
      pos='relative'
      zIndex={1}
      overflow='hidden'
      w='100%'
      minH={`${estimatedHeight}px`}
      px={0}
      bg='inherit'
      _focus={{ outline: 'none', boxShadow: 'none' }}
      whiteSpace='pre-line'
      resize='none'
      onChange={(e) => {
        onChangeProp(e.target.value);
        setTimeout(adjustHeight, 0);
      }}
      value={value || ''}
    />
  );
};

const MessageChat = ({ data, status, isLast, user, cancelEditRef }: MessageChatProps) => {
  const toast = useToast();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    current,
    processingStreamingChunkStatusTask,
    status: taskStatus,
  } = useSelector((store) => store.task);

  const [isHovering, setIsHovering] = useState(false);
  const [question, setQuestion] = useState('');
  const models = useSelector((state: any) => state.model?.models || []);
  const [displayModelName, setDisplayModelName] = useState<string | null>(null);

  const isError = data?.status === 'error';
  const isLastMessageWithoutAnswer = Boolean(isLast && !data.answer && data.status !== 'error');
  const isShowEditIcon = Boolean(
    isHovering && isLast && status !== TASK_STATUS.GENERATE_ANSWER && data.answer
  );
  const lastAnswer = data.answer ? (
    <MarkdownRenderer content={data.answer} />
  ) : !isError &&
    (taskStatus === TASK_STATUS.LOADING ||
      taskStatus === TASK_STATUS.GENERATE_ANSWER ||
      processingStreamingChunkStatusTask ||
      data.status === 'loading' ||
      isLastMessageWithoutAnswer ||
      !data.status) ? (
    <Box pt={2}>
      <Spinner color='gray.400' size='sm' />
    </Box>
  ) : (
    <span>{TEXT_CONSTANT.SUMMARY.ERROR_ANSWER}</span>
  );

  const onMouseEnter = () => {
    setIsHovering(true);
  };

  const onMouseLeave = () => {
    setIsHovering(false);
  };

  // Copy the summary answer to the clipboard (mockup parity: copy icon next to Export to Word).
  const { hasCopied, onCopy: onCopyAnswer } = useClipboard(data?.answer ?? '');

  const onExportToWord = () => {
    exportAnswerToWord({
      content: data.answer ?? '',
      title: data.question,
      fileName: data.question,
    }).catch(() => {
      toast({
        title: t('common.exportWordError', { defaultValue: 'Failed to export to Word' }),
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    });
  };

  const onSubmidEditQuestion = (question: string) => {
    if (current.selectedFiles.length === 0) {
      toast({
        title: `Please add files before submit`,
        status: 'warning',
        position: 'bottom-right',
        isClosable: true,
      });
      return;
    }
    if (!question) {
      toast({
        title: `Input must not empty`,
        status: 'warning',
        position: 'bottom-right',
        isClosable: true,
      });
      return;
    }
    dispatch(taskAction.setTimeOutForFinalAnswerTask(true));
    dispatch(taskAction.updateProcessingStreamingChunkStatusTask(true));
    dispatch(taskAction.editQuestion({ question, created_at: new Date().toISOString() }));
  };

  useEffect(() => {
    if (data?.requested_model && models.length > 0) {
      // Use requested_model from API response if available
      const model = models.find(
        (m: Model) =>
          m.model_name === data.requested_model ||
          m.name === data.requested_model ||
          m.id === data.requested_model
      );
      if (model) {
        setDisplayModelName(model.display_name || model.name);
      } else {
        // Fallback to requested_model if not found in models
        setDisplayModelName(data.requested_model);
      }
    } else if (data?.model?.name) {
      // Fallback to old model.name if requested_model not available
      const model = models.find(
        (m: Model) => m.name === data.model?.name || m.id === data.model?.name
      );
      setDisplayModelName(model?.display_name || data.model.name);
    } else if (!data?.answer && current?.selectedModelId && models.length > 0) {
      // When waiting for response, use selectedModelId from current task
      const model = models.find((m: Model) => m.id === current.selectedModelId);
      if (model) {
        setDisplayModelName(model.display_name || model.name);
      }
    } else {
      setDisplayModelName(null);
    }
  }, [data?.requested_model, data?.model, data?.answer, current?.selectedModelId, models]);

  useEffect(() => {
    setQuestion(data?.question);
  }, [data?.question]);

  useEffect(() => {
    if (data.answer) {
      dispatch(taskAction.updateProcessingStreamingChunkStatusTask(false));
      dispatch(taskAction.setTimeOutForFinalAnswerTask(false));
      dispatch(taskAction.clearStreamingAnswerChunksTask());
    }
  }, [data.answer, current.task_id]);

  return (
    <>
      {/* User question — DS primary bubble, right-aligned */}
      <Flex align='flex-start' justify='flex-end' gap={3} pt={{ base: 5 }}>
        <Box pos='relative' maxW={{ base: '80%', md: '70%' }} fontSize='base'>
          <Editable
            key={data?.question}
            isPreviewFocusable={false}
            onCancel={() => {
              setQuestion(data?.question || '');
            }}
            onChange={(value) => setQuestion(value)}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onSubmit={onSubmidEditQuestion}
            selectAllOnFocus={false}
            submitOnBlur={false}
            value={question}
          >
            <Box
              pos='relative'
              mb={1}
              px={5}
              py={3}
              pe={10}
              color='primary.on'
              lineHeight={1.6}
              bg='primary.solid'
              opacity={isError ? 0.5 : 1}
              borderRadius='14px 14px 4px 14px'
              shadow='ds_xs'
              whiteSpace='pre-line'
            >
              {isLast ? (
                <>
                  <EditablePreview maxW='100%' whiteSpace='pre-line' />
                  <Box
                    as={EditableInput}
                    sx={{
                      display: 'none !important',
                      visibility: 'hidden !important',
                      position: 'absolute',
                      opacity: 0,
                      pointerEvents: 'none',
                    }}
                  />
                  <EditableTextarea value={question} onChange={setQuestion} />
                </>
              ) : (
                <>{data?.question}</>
              )}
            </Box>
            <Flex align='center' justify='flex-end' gap={2} color='text.subtle' fontSize='xs'>
              <EditableControls
                isShowEditIcon={isShowEditIcon}
                cancelRef={cancelEditRef}
                value={question}
              />
              {isError && (
                <Text as='span' color='state.error'>
                  Error sent
                </Text>
              )}
              {timeFormat(data?.created_at)}
            </Flex>
          </Editable>
        </Box>
        <Avatar
          color='primary.on'
          bg='primary.solid'
          name={user.name?.replace(/\s+/g, ' ') || user.fullName}
          size='sm'
        />
      </Flex>

      {/* Assistant answer — branded mark + DS surface card */}
      <Flex align='flex-start' justify='flex-start' gap={3} overflowX='hidden' w='100%' mt={3}>
        <Flex
          align='center'
          justify='center'
          flexShrink={0}
          w='32px'
          h='32px'
          color='primary.on'
          fontFamily='heading'
          fontWeight={700}
          bg='primary.solid'
          borderRadius='md'
          shadow='ds_xs'
        >
          A
        </Flex>
        {processingStreamingChunkStatusTask &&
        !data.answer &&
        !current.answerChunks.part_of_answer ? (
          <Box flex={1} minW={0}>
            {displayModelName && (
              <Text mb={1} color='text.muted' fontSize='sm' fontWeight={600} textAlign='left'>
                {displayModelName}
              </Text>
            )}
            <Flex
              align='center'
              gap={3}
              px={5}
              py={4}
              color='text.muted'
              fontSize='base'
              bg='bg.surface'
              borderWidth='1px'
              borderColor='border.default'
              borderRadius='14px 14px 14px 4px'
              shadow='ds_sm'
              whiteSpace='pre-line'
            >
              <Spinner color='primary.solid' size='sm' /> {t('common.loading_answer')}
            </Flex>
          </Box>
        ) : (
          <Box flex={1} minW={0}>
            {displayModelName && (
              <Text mb={1} color='text.muted' fontSize='sm' fontWeight={600} textAlign='left'>
                {displayModelName}
              </Text>
            )}
            <Box
              className='mess_container'
              px={{ base: 4, md: 6 }}
              py={4}
              color='text.default'
              fontSize='base'
              lineHeight={1.65}
              textAlign='left'
              bg='bg.surface'
              borderWidth='1px'
              borderColor='border.default'
              borderRadius='14px 14px 14px 4px'
              shadow='ds_sm'
              whiteSpace='pre-line'
            >
              {!data.answer && current.answerChunks.part_of_answer ? <TypingText /> : lastAnswer}
            </Box>
            {data.answer && (
              <Flex align='center' gap={2} mt={1}>
                <Tooltip
                  closeOnClick={false}
                  label={
                    hasCopied
                      ? t('common.copied', { defaultValue: 'Copied' })
                      : t('common.copy', { defaultValue: 'Copy' })
                  }
                >
                  <IconButton
                    color={hasCopied ? 'state.success' : undefined}
                    aria-label={t('common.copy', { defaultValue: 'Copy' })}
                    icon={hasCopied ? <Check size={15} /> : <Copy size={15} />}
                    onClick={onCopyAnswer}
                    size='ds-sm'
                    variant='ds-ghost'
                  />
                </Tooltip>
                <Tooltip label={t('common.exportWord', { defaultValue: 'Export to Word' })}>
                  <IconButton
                    aria-label={t('common.exportWord', { defaultValue: 'Export to Word' })}
                    icon={<FileDown size={15} />}
                    onClick={onExportToWord}
                    size='ds-sm'
                    variant='ds-ghost'
                  />
                </Tooltip>
                <Text color='text.subtle' fontSize='xs' textAlign='left'>
                  {timeFormat(data?.updated_at)}
                </Text>
              </Flex>
            )}
          </Box>
        )}
      </Flex>
    </>
  );
};

interface EditableControlsProps {
  isShowEditIcon: boolean;
  value: string;
  cancelRef: React.RefObject<HTMLButtonElement>;
}

const EditableControls = ({ isShowEditIcon, cancelRef, value }: EditableControlsProps) => {
  const { t } = useTranslation();
  const { isEditing, getSubmitButtonProps, getCancelButtonProps, getEditButtonProps } =
    useEditableControls();
  return isEditing ? (
    <ButtonGroup justifyContent='end' size='ds-sm' spacing={2}>
      <Button {...getCancelButtonProps()} ref={cancelRef} size='ds-sm' variant='ds-ghost'>
        {t('common.cancel')}
      </Button>
      <Button {...getSubmitButtonProps()} isDisabled={!value} size='ds-sm' variant='ds-primary'>
        {t('common.update')}
      </Button>
    </ButtonGroup>
  ) : (
    <>
      {isShowEditIcon && (
        <Tooltip label={t('common.editText')}>
          <Flex
            pos='absolute'
            top={2}
            right={3}
            align='center'
            justify='center'
            w='26px'
            h='26px'
            color='primary.on'
            borderRadius='sm'
            _hover={{ cursor: 'pointer', bg: 'whiteAlpha.300' }}
            {...getEditButtonProps()}
          >
            <Pencil size={15} />
          </Flex>
        </Tooltip>
      )}
    </>
  );
};

export default MessageChat;
